import { cardinalRotation, rotationAt, rotationCuesFromTs } from './tsRotation.js';

/**
 * Copie de lecture MP4 d'un flux TS déchiffré, produite dans le navigateur
 * (WebCodecs, via Mediabunny chargé à la demande) : le serveur n'a pas la clé.
 * Chaque image est redressée au quart de tour, comme le mode fixe du direct ;
 * l'audio est recopié sans réencodage. Ce n'est pas une pièce originale : le
 * dossier garde les segments d'origine et le TS sans perte.
 */
export async function tsToMp4({ tsBlob, onProgress = () => {} }) {
    const {
        Input, Output, Conversion, BlobSource, BufferTarget, Mp4OutputFormat, ALL_FORMATS, canEncodeVideo,
    } = await import('mediabunny');
    const cues = rotationCuesFromTs(await tsBlob.arrayBuffer());
    const input = new Input({ source: new BlobSource(tsBlob), formats: ALL_FORMATS });
    const videoTrack = await input.getPrimaryVideoTrack();
    let video;
    if (videoTrack) {
        if (!(await canEncodeVideo('avc'))) {
            throw new Error('Ce navigateur ne sait pas encoder de vidéo (WebCodecs). Utilisez Chrome ou Edge récents ; le fichier TS reste disponible.');
        }
        const width = videoTrack.displayWidth;
        const height = videoTrack.displayHeight;
        const angles = new Set(cues.map(cue => cardinalRotation(cue.deg)));
        if (!angles.size) angles.add(0);
        const quarter = [...angles].map(angle => angle % 180 !== 0);
        // Orientation constante : image tournée ; orientation qui change : carré
        // (bandes noires), pour que chaque image garde ses proportions.
        const [outWidth, outHeight] = quarter.every(Boolean) ? [height, width]
            : quarter.every(value => !value) ? [width, height]
                : [Math.max(width, height), Math.max(width, height)];
        const canvas = new OffscreenCanvas(outWidth, outHeight);
        const context = canvas.getContext('2d');
        let firstTimestamp = null;
        video = {
            codec: 'avc',
            forceTranscode: true,
            processedWidth: outWidth,
            processedHeight: outHeight,
            process: sample => {
                if (firstTimestamp === null) firstTimestamp = sample.timestamp;
                const angle = cardinalRotation(rotationAt(cues, sample.timestamp - firstTimestamp));
                context.fillStyle = '#000';
                context.fillRect(0, 0, outWidth, outHeight);
                context.save();
                context.translate(outWidth / 2, outHeight / 2);
                context.rotate((angle * Math.PI) / 180);
                sample.draw(context, -width / 2, -height / 2, width, height);
                context.restore();
                return canvas;
            },
        };
    }
    const output = new Output({ format: new Mp4OutputFormat({ fastStart: 'in-memory' }), target: new BufferTarget() });
    const conversion = await Conversion.init({ input, output, ...(video ? { video } : {}) });
    if (!conversion.isValid) {
        throw new Error('Conversion MP4 impossible dans ce navigateur ; le fichier TS reste disponible.');
    }
    conversion.onProgress = progress => onProgress(`conversion MP4 ${Math.round(progress * 100)} %`);
    await conversion.execute();
    return new Blob([output.target.buffer], { type: 'video/mp4' });
}
